import { useEffect, useRef, useState } from 'react';
import { Copy } from '@react-apps/design-system';
import { IconButton } from './icon-button';

/**
 * Botão de copiar com feedback momentâneo. O feedback só diz "Copiado!" quando
 * o navegador confirmou: contexto não seguro ou permissão negada rejeitam o
 * `writeText`, e um sucesso falso faria o operador colar nada.
 *
 * `icon` troca o texto pelo ícone `Copy`, com o `label` como nome acessível. O
 * feedback mora numa região `aria-live` nos dois modos: sem ela, quem usa
 * leitor de tela clica e não ouve se copiou.
 */
export function CopyButton({
  text,
  label,
  icon = false,
}: {
  text: string;
  label: string;
  icon?: boolean;
}) {
  const [feedback, setFeedback] = useState<string | null>(null);
  // Um timer por vez: sem cancelar, o do primeiro clique apagava o feedback do
  // segundo antes da hora.
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setFeedback('Copiado!');
    } catch {
      setFeedback('Não copiou — selecione o texto');
    }
    // Cancela depois do `await`: dois cliques antes do navegador responder
    // deixariam o timer do primeiro solto.
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setFeedback(null), 2000);
  };
  return (
    <span className="inline-flex shrink-0 items-center gap-1">
      {icon ? (
        <>
          <IconButton
            label={label}
            icon={<Copy size="sm" />}
            onClick={() => void copy()}
          />
          <span
            aria-live="polite"
            className="text-(--color-text-secondary) text-xs font-normal"
          >
            {feedback}
          </span>
        </>
      ) : (
        <button
          type="button"
          onClick={() => void copy()}
          className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-xs underline"
        >
          {/* O próprio texto do botão é o feedback: anunciá-lo aqui evita um
              segundo nó com o mesmo "Copiado!". */}
          <span aria-live="polite">{feedback ?? label}</span>
        </button>
      )}
    </span>
  );
}
